import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  FormWithRedirect,
  SaveButton,
  useNotify,
  useQuery,
  useRedirect,
  useRefresh,
  useUpdate,
} from 'react-admin';
import { Link, useParams } from 'react-router-dom';
import { Field } from 'react-final-form';
import CreatableSelect from 'react-select/creatable';
import DialogActions from '@material-ui/core/DialogActions';
import Portal from '@material-ui/core/Portal';
import IconCancel from '@material-ui/icons/Cancel';

import dataProvider from '../../dataProvider';
import { ServiceAreaContext } from '../../Context';

const ServiceVehicleEdit = ({ basePath }) => {
  const { id: service_vehicle_id } = useParams();
  const {
    serviceVehicle,
    setServiceVehicle,
    editServiceAreas,
    setEditServiceAreas,
    portalRef,
  } = useContext(ServiceAreaContext);
  const [options, setOptions] = useState(null);
  const [update, { loading }] = useUpdate('service-vehicle');
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const { data, loading: getLoading, error } = useQuery({
    type: 'getOne',
    resource: 'service-vehicle',
    payload: { id: service_vehicle_id },
  });
  useEffect(() => {
    if (data) {
      setServiceVehicle(data);
      setEditServiceAreas(
        data.service_areas.map((service_area) => service_area.postal_code),
      );
    }
  }, [service_vehicle_id, data, setServiceVehicle, setEditServiceAreas]);
  useEffect(() => {
    if (!options) {
      dataProvider
        .getList('service-area', {
          sort: { field: 'postal_code', order: 'ASC' },
          pagination: { page: 1, perPage: 1000 },
        })
        .then((response) => {
          setOptions(
            response.data.map((res) => {
              return { value: res.postal_code, label: res.postal_code };
            }),
          );
        });
    }
  }, [options]);
  if (getLoading || loading || !options) {
    return 'Loading...';
  }
  if (error) {
    return (
      <p>
        Something went wrong{' '}
        <Button
          label="ra.action.cancel"
          component={Link}
          to={{
            pathname: '/service-area',
          }}
        >
          <IconCancel />
        </Button>
      </p>
    );
  }
  const serviceAreas = (editServiceAreas || []).map((service_area) => {
    return {
      value: service_area,
      label: service_area,
    };
  });
  const inputIsInvalid = (service_areas_arr) => {
    if (service_areas_arr.length === 0) {
      notify(`Error: Enter at least one zip code`, 'warning');
      return true;
    }
    for (let i = 0; i < service_areas_arr.length; i += 1) {
      const zip = service_areas_arr[i];
      // eslint-disable-next-line no-restricted-globals
      if (isNaN(zip) || zip.length !== 5) {
        notify(`Error: ${zip} is not a valid zip`, 'warning');
        return true;
      }
    }
    return false;
  };
  const handleSubmit = async () => {
    const invalid = inputIsInvalid(editServiceAreas);
    if (invalid) {
      return;
    }
    notify('Saving...');
    update(
      {
        payload: {
          id: serviceVehicle.id,
          data: { service_area: editServiceAreas },
          previousData: serviceVehicle,
        },
      },
      {
        onSuccess: () => {
          refresh();
          redirect('list', basePath);
          notify('Service vehicle updated successfully');
        },
        onFailure: ({ error: err }) => {
          notify(err.message, 'error');
        },
      },
    );
  };
  const handleChange = (val) => {
    if (val) {
      setEditServiceAreas(val.map((datum) => datum.value));
    }
  };
  const handleInputChange = (val) => {
    const addedAreas = val
      .replace(/\s/g, '')
      .split(',')
      .filter((area) => area !== '');
    if (addedAreas.length > 1) {
      const invalid = inputIsInvalid(addedAreas);
      if (invalid) {
        return '';
      }
      const uniqueAreas = Array.from(
        new Set([...editServiceAreas, ...addedAreas]),
      );
      handleChange(
        uniqueAreas.map((area) => {
          return { value: area, label: area };
        }),
      );
      return '';
    }
    return val;
  };
  const handleCreate = (input) => {
    const invalid = inputIsInvalid([input]);
    if (invalid) {
      return;
    }
    setOptions([...options, { value: input, label: input }]);
    setEditServiceAreas([...editServiceAreas, input]);
  };
  return (
    <>
      {service_vehicle_id && serviceVehicle && (
        <FormWithRedirect
          resource="service-vehicle"
          record={serviceVehicle}
          save={handleSubmit}
          render={(formProps) => {
            return (
              <>
                <Field name="service_areas">
                  {(props) => {
                    return (
                      <CreatableSelect
                        name={props.input.name}
                        isMulti
                        onChange={handleChange}
                        options={options}
                        onCreateOption={handleCreate}
                        value={serviceAreas}
                        minMenuHeight={150}
                        maxMenuHeight={150}
                        openMenuOnClick={false}
                        onInputChange={handleInputChange}
                      />
                    );
                  }}
                </Field>
                <Portal container={portalRef.current}>
                  <DialogActions>
                    <Button
                      label="ra.action.cancel"
                      component={Link}
                      to={{
                        pathname: '/service-area',
                      }}
                    >
                      <IconCancel />
                    </Button>
                    <SaveButton
                      handleSubmitWithRedirect={
                        formProps.handleSubmitWithRedirect
                      }
                      saving={formProps.saving}
                    />
                  </DialogActions>
                </Portal>
              </>
            );
          }}
        />
      )}
    </>
  );
};

export default ServiceVehicleEdit;
